import { isValidUrl } from "./url";
import isValidEmail from "./email";

/**
 * Validates a URL or email address
 * @param {string} url - URL or email to validate
 * @returns {{ isValid: boolean, error: string|null }} Validation result
 *
 * Empty or whitespace-only input returns isValid: true (no validation error).
 * This allows optional fields to be left empty; required-field checks should
 * be done separately by the caller.
 */
export const validateUrlOrEmail = (url) => {
  if (!url || url.trim() === "") {
    return { isValid: true, error: null };
  }

  const trimmedUrl = url.trim();

  // Allow mailto: links
  if (trimmedUrl.toLowerCase().startsWith("mailto:")) {
    const emailPart = trimmedUrl.substring(7).split("?")[0].trim();
    if (isValidEmail(emailPart)) {
      return { isValid: true, error: null };
    }
    return {
      isValid: false,
      error: "Please enter a valid email address after mailto:",
    };
  }

  // Allow plain email addresses (will be converted to mailto:)
  if (isValidEmail(trimmedUrl)) {
    return { isValid: true, error: null };
  }

  // Validate as URL
  if (isValidUrl(trimmedUrl)) {
    return { isValid: true, error: null };
  }

  return {
    isValid: false,
    error: "Please enter a valid URL or email address",
  };
};

/**
 * Normalizes email addresses to mailto: format
 * @param {string} href - URL or email address
 * @returns {string} Normalized href (email addresses converted to mailto:)
 */
export const normalizeEmailToMailto = (href) => {
  if (!href) {
    return href;
  }

  const trimmedHref = href.trim();

  // If it's already mailto:, return as is
  if (trimmedHref.toLowerCase().startsWith("mailto:")) {
    return trimmedHref;
  }

  // If it's a plain email, convert to mailto:
  if (isValidEmail(trimmedHref)) {
    return `mailto:${trimmedHref}`;
  }

  return trimmedHref;
};
